// Converting balances into the user's base currency (backlog 3.10, D-056).
// The rule: an amount with no usable rate is never added to a total as if it were
// already in the base currency. It is left out and reported, so the screen can say so.

export type Rates = Record<string, number>; // units of that currency per 1 base unit

export function convertAmount(
  amount: number,
  fromCurrency: string,
  baseCurrency: string,
  rates: Rates,
  manualRate?: number | null,
): number | null {
  if (fromCurrency === baseCurrency) return amount;
  if (manualRate != null && manualRate > 0) return amount * manualRate;
  const rate = rates[fromCurrency];
  return rate ? amount / rate : null;
}

export type Holding = { amount: number; currency: string; manualRate?: number | null };
export type Excluded = { currency: string; amount: number };

export function sumConverted(holdings: Holding[], baseCurrency: string, rates: Rates) {
  let total = 0;
  const excluded = new Map<string, number>();
  for (const h of holdings) {
    const value = convertAmount(h.amount, h.currency, baseCurrency, rates, h.manualRate);
    if (value === null) excluded.set(h.currency, (excluded.get(h.currency) ?? 0) + h.amount);
    else total += value;
  }
  return {
    total,
    excluded: [...excluded].map(([currency, amount]): Excluded => ({ currency, amount })),
  };
}

/** "¥150,000 and 0.5 BTC": amounts in their own currency, for the "Excludes …" line. */
export function describeExcluded(excluded: Excluded[]): string {
  const parts = excluded.map(({ currency, amount }) =>
    isIsoCurrency(currency)
      ? new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 2 }).format(amount)
      : // Crypto and other non-ISO codes: keep their small decimals, "0.00012 BTC".
        `${amount.toLocaleString(undefined, { maximumFractionDigits: 8 })} ${currency}`,
  );
  return parts.length <= 1 ? (parts[0] ?? "") : `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;
}

// Intl formats any three-letter code (it would print "BTC 0.00"), so check the real list.
// supportedValuesOf is newer than the TypeScript lib this app targets, so look it up loosely.
const supportedValuesOf = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
const ISO_CURRENCIES: Set<string> | null =
  typeof supportedValuesOf === "function" ? new Set(supportedValuesOf("currency")) : null;

export function isIsoCurrency(code: string): boolean {
  return ISO_CURRENCIES ? ISO_CURRENCIES.has(code) : /^[A-Z]{3}$/.test(code) && !["BTC", "ETH", "SOL", "XRP", "ADA", "BNB", "DOT"].includes(code);
}
